import React, { useState, useMemo } from "react";
import useSWR, { mutate } from "swr";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, PageBody, EmptyState } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash } from "@phosphor-icons/react";
import { Badge } from "./Projects";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORY_LABEL = {
  roofing_system: { en: "Roofing Systems", fr: "Systèmes de toiture" },
  siding_system: { en: "Siding Systems", fr: "Systèmes de revêtement" },
  soffit_fascia: { en: "Soffit & Fascia", fr: "Soffite & Fascia" },
  roof_flashing: { en: "Roof Flashing", fr: "Solins toiture" },
  siding_trim: { en: "Siding Trim", fr: "Moulures revêtement" },
  underlayment: { en: "Underlayment", fr: "Sous-couches" },
  deck_repair: { en: "Deck Repair", fr: "Réparation pontage" },
  fasteners: { en: "Fasteners", fr: "Attaches" },
  sealants: { en: "Sealants", fr: "Scellants" },
  ventilation: { en: "Ventilation", fr: "Ventilation" },
  gutters: { en: "Gutters", fr: "Gouttières" },
  snow_ice: { en: "Snow & Ice", fr: "Neige & Glace" },
  penetrations: { en: "Skylights / Pipes", fr: "Puits de lumière / Évents" },
  surcharges: { en: "Surcharges", fr: "Suppléments" },
  safety_equipment: { en: "Safety / Setup", fr: "Sécurité / Installation" },
  service: { en: "Service / Labor", fr: "Service / Main-d'œuvre" },
  custom_metal: { en: "Custom Metal", fr: "Métal sur mesure" },
  labor: { en: "Labor", fr: "Main-d'œuvre" },
  other: { en: "Other", fr: "Autre" },
};

export default function Proposals() {
  const { t, lang } = useI18n();
  const location = useLocation();
  const { data: proposals = [] } = useSWR("/proposals", fetcher);
  const { data: projects = [] } = useSWR("/projects", fetcher);
  const { data: catalog = [] } = useSWR("/catalog", fetcher);
  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState(location.state?.project_id || "");
  const [title, setTitle] = useState(lang === "fr" ? "Soumission de toiture" : "Roofing Proposal");
  const [scope, setScope] = useState("");
  const [lines, setLines] = useState([
    { label: "Bardeaux architecturaux", category: "material", qty: 75, unit: "bundle", unit_price: 38, total: 75 * 38 },
    { label: "Main-d'œuvre", category: "labor", qty: 40, unit: "hour", unit_price: 65, total: 40 * 65 },
  ]);
  const [markup, setMarkup] = useState(20);
  const [tax, setTax] = useState(14.975);
  const [deposit, setDeposit] = useState(30);
  const [validUntil, setValidUntil] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10);
  });
  const [terms, setTerms] = useState(lang === "fr"
    ? "Conditions de paiement : acompte de 30 %, solde dû à la fin des travaux. Garantie de main-d'œuvre : 5 ans."
    : "Payment terms: 30% deposit, balance due on completion. Workmanship warranty: 5 years.");

  const updateLine = (i, patch) => {
    const next = [...lines]; next[i] = { ...next[i], ...patch };
    next[i].total = Math.round(Number(next[i].qty || 0) * Number(next[i].unit_price || 0) * 100) / 100;
    setLines(next);
  };

  const subtotal = useMemo(() => lines.reduce((a, b) => a + Number(b.total || 0), 0), [lines]);
  const marked = subtotal * (1 + markup / 100);
  const total = marked * (1 + tax / 100);
  const depositAmount = total * deposit / 100;

  const groupedCatalog = useMemo(() => {
    const g = {};
    catalog.filter((c) => c.active).forEach((c) => { (g[c.category] ||= []).push(c); });
    return g;
  }, [catalog]);

  const visibleCategories = useMemo(
    () => Object.keys(CATEGORY_LABEL).filter((k) => groupedCatalog[k]),
    [groupedCatalog]
  );

  const addFromCatalog = (itemId) => {
    const c = catalog.find((x) => x.id === itemId);
    if (!c) return;
    setLines([...lines, {
      label: (lang === "fr" && c.label_fr) ? c.label_fr : c.label,
      category: c.category,
      qty: 1,
      unit: c.unit,
      unit_price: c.unit_price,
      total: c.unit_price,
    }]);
  };

  const submit = async () => {
    if (!projectId || lines.length === 0) return toast.error(lang === "fr" ? "Projet et lignes requis" : "Project and lines required");
    try {
      await api.post("/proposals", {
        project_id: projectId,
        title,
        scope,
        lines: lines.filter(l => l.label),
        markup_pct: Number(markup),
        tax_pct: Number(tax),
        deposit_pct: Number(deposit),
        terms,
        valid_until: validUntil,
        photo_file_ids: [],
      });
      toast.success(lang === "fr" ? "Soumission créée" : "Proposal created");
      setOpen(false);
      mutate("/proposals");
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="proposals-page">
      <PageHeader
        eyebrow="// 10 / Proposals"
        title={t("proposals.title")}
        sub={t("proposals.sub")}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-proposal-button" className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
                <Plus size={14} className="mr-2" weight="bold" /> {t("proposals.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-zinc-300 max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display font-black text-2xl tracking-tight">{t("proposals.new")}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <Field label={t("nav.projects")}>
                  <Select value={projectId} onValueChange={setProjectId}>
                    <SelectTrigger data-testid="prop-project-select" className="rounded-none"><SelectValue placeholder="…" /></SelectTrigger>
                    <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={t("proposals.validUntil")}><Input data-testid="prop-valid" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="rounded-none mono" /></Field>
                <Field label="Title / Titre"><Input data-testid="prop-title" value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-none" /></Field>
                <Field label={t("proposals.deposit")}><Input data-testid="prop-deposit" type="number" value={deposit} onChange={(e) => setDeposit(Number(e.target.value))} className="rounded-none mono" /></Field>
              </div>
              <div className="mt-4">
                <Label className="label-eyebrow text-zinc-600 mb-1.5 block">{t("proposals.scope")}</Label>
                <Textarea data-testid="prop-scope" value={scope} onChange={(e) => setScope(e.target.value)} placeholder={lang === "fr" ? "Décrivez les travaux à effectuer…" : "Describe the work to be performed…"} rows={3} className="rounded-none" />
              </div>

              <div className="mt-4 border border-zinc-200">
                <div className="border-b border-zinc-200 px-3 py-2 flex justify-between items-center">
                  <p className="label-eyebrow text-zinc-500">Lines / Lignes</p>
                  <Select onValueChange={addFromCatalog}>
                    <SelectTrigger className="rounded-none w-80 h-8 text-xs" data-testid="prop-catalog-select"><SelectValue placeholder={lang === "fr" ? "+ Depuis le catalogue C.H.C." : "+ From C.H.C. catalog"} /></SelectTrigger>
                    <SelectContent className="max-h-96">
                      {visibleCategories.map((k) => (
                        <SelectGroup key={k}>
                          <SelectLabel className="label-eyebrow text-orange-600">{CATEGORY_LABEL[k][lang === "fr" ? "fr" : "en"]}</SelectLabel>
                          {groupedCatalog[k].map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {(lang === "fr" && c.label_fr) ? c.label_fr : c.label}
                              <span className="text-zinc-400 ml-2 mono text-xs">· {fmt$(c.unit_price)}/{c.unit}</span>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-12 px-3 py-2 border-b border-zinc-200 label-eyebrow text-zinc-500 bg-zinc-50">
                  <div className="col-span-5">Item</div><div className="col-span-2">Qty</div><div className="col-span-2">Unit</div><div className="col-span-2">$</div><div className="col-span-1 text-right">{t("common.total")}</div>
                </div>
                {lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 px-3 py-2 border-b border-zinc-100 items-center">
                    <Input className="col-span-5 rounded-none" value={l.label} onChange={(e) => updateLine(i, { label: e.target.value })} />
                    <Input type="number" className="col-span-2 rounded-none mono" value={l.qty} onChange={(e) => updateLine(i, { qty: e.target.value })} />
                    <Input className="col-span-2 rounded-none mono text-xs" value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })} />
                    <Input type="number" step="0.01" className="col-span-2 rounded-none mono" value={l.unit_price} onChange={(e) => updateLine(i, { unit_price: e.target.value })} />
                    <div className="col-span-1 mono text-xs text-right flex items-center justify-end gap-1">
                      {fmt$(l.total)}
                      <button onClick={() => setLines(lines.filter((_, x) => x !== i))} className="text-zinc-400 hover:text-orange-600"><Trash size={12} /></button>
                    </div>
                  </div>
                ))}
                <div className="p-2">
                  <Button variant="outline" onClick={() => setLines([...lines, { label: "", category: "material", qty: 1, unit: "ea", unit_price: 0, total: 0 }])} className="rounded-none border-zinc-300 text-xs uppercase font-bold tracking-wider">
                    <Plus size={14} className="mr-2" /> Add / Ajouter
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-3 mt-4">
                <Field label="Markup %"><Input data-testid="prop-markup" type="number" value={markup} onChange={(e) => setMarkup(Number(e.target.value))} className="rounded-none mono" /></Field>
                <Field label="Tax % (TPS+TVQ)"><Input data-testid="prop-tax" type="number" step="0.001" value={tax} onChange={(e) => setTax(Number(e.target.value))} className="rounded-none mono" /></Field>
                <div className="col-span-2 text-right">
                  <p className="label-eyebrow text-zinc-500">{lang === "fr" ? "Total client" : "Customer total"}</p>
                  <p className="font-display font-black text-3xl text-orange-600 tracking-tighter">{fmt$(total)}</p>
                  <p className="mono text-xs text-zinc-600 mt-1">{lang === "fr" ? "Acompte" : "Deposit"}: {fmt$(depositAmount)}</p>
                </div>
              </div>
              <div className="mt-4">
                <Label className="label-eyebrow text-zinc-600 mb-1.5 block">{t("proposals.terms")}</Label>
                <Textarea data-testid="prop-terms" value={terms} onChange={(e) => setTerms(e.target.value)} rows={2} className="rounded-none" />
              </div>
              <Button data-testid="prop-save" onClick={submit} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider w-full h-11 mt-4">{t("common.save")}</Button>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        {proposals.length === 0 ? (
          <EmptyState title={t("proposals.empty")} sub={t("proposals.sub")} />
        ) : (
          <div className="border border-zinc-200">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-zinc-200 label-eyebrow text-zinc-500 bg-zinc-50">
              <div className="col-span-1 mono text-[10px]">#</div>
              <div className="col-span-4">{t("projects.customer")}</div>
              <div className="col-span-2">{t("common.date")}</div>
              <div className="col-span-2">{t("common.status")}</div>
              <div className="col-span-3 text-right">{t("common.total")}</div>
            </div>
            {proposals.map((p, i) => (
              <Link key={p.id} to={`/app/proposals/${p.id}`} data-testid={`proposal-row-${p.id}`} className="grid grid-cols-12 px-5 py-4 border-b border-zinc-100 hover:bg-zinc-50 items-center">
                <div className="col-span-1 mono text-xs text-zinc-500">{String(proposals.length - i).padStart(3, "0")}</div>
                <div className="col-span-4 font-semibold">{p.customer_name} <span className="text-zinc-500 text-xs font-normal ml-2">{p.title}</span></div>
                <div className="col-span-2 mono text-xs text-zinc-600">{p.created_at?.slice(0,10)}</div>
                <div className="col-span-2"><Badge status={p.status} /></div>
                <div className="col-span-3 text-right mono font-semibold">{fmt$(p.total)}</div>
              </Link>
            ))}
          </div>
        )}
      </PageBody>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow text-zinc-600 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
